/* eslint-disable @typescript-eslint/no-floating-promises */
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  SmartForm,
  SmartSubmit,
  SmartTextField,
  useSmartForm,
} from "@nodeon/react-components";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { PermissionItemDto, useOrganizationApi } from "~/api/GeneratedApi";
import { setTitle } from "~/redux/reducers/generalReducer";
import { useCustomMutation } from "~/utils/hooks/useCustomMutation";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { PermissionEdit } from "../../components/PermissionEdit";

interface Params {
  orgId: string;
  orgRoleId: string;
}

interface FormProps {
  name: string;
}

const OrganizationRolePage = (): JSX.Element => {
  const { orgId, orgRoleId } = useParams<Params>();
  const organizationId = parseInt(orgId, 10);
  const roleId = parseInt(orgRoleId, 10);
  const { t } = useTranslation("organization-role");
  const dispatch = useDispatch();

  const organizationApi = useOrganizationApi();

  const { data: organizationData } = useCustomQuery(
    { key: "organization", id: organizationId },
    () => organizationApi.getOrganization(organizationId),
  );

  const {
    isLoading: isLoadingRoleData,
    data: roleData,
    refetch: refetchRoleData,
  } = useCustomQuery({ key: "organization-role", organizationId, roleId }, () =>
    organizationApi.getOrganizationRole(organizationId, roleId),
  );

  const defaultValues = useMemo(
    () => ({ name: roleData?.roleName ?? "" }),
    [roleData?.roleName],
  );

  const formMethods = useSmartForm<FormProps>({
    defaultValues,
  });

  const {
    isLoading,
    data: permissions,
    refetch: refetchPermissions,
  } = useCustomQuery(
    { key: "organization-role-permissions", organizationId, roleId },
    () =>
      organizationApi.getOrganizationRolePermissions(organizationId, roleId),
    { initialData: [] },
  );

  useEffect(() => {
    if (!organizationData || !roleData) return;
    dispatch(
      setTitle([
        {
          label: t("organization-management:organizations"),
          to: "/organizations",
        },
        {
          label: organizationData.name,
          to: `/organizations/${organizationId}/roles`,
        },
        { label: roleData.roleName! },
      ]),
    );
  }, [dispatch, organizationData, t, roleData, organizationId]);

  const { mutateAsync: editPermissions } = useCustomMutation(
    (mut: PermissionItemDto[]) =>
      organizationApi.setOrganizationRolePermissions(
        organizationId,
        roleId,
        mut,
      ),
    {
      onSuccess: () => {
        refetchPermissions();
      },
    },
  );

  const { mutateAsync: editOrganizationRole } = useCustomMutation(
    (roleName: string) =>
      organizationApi.editOrganizationRole(
        { roleName },
        organizationId,
        roleId,
      ),
    {
      onSuccess: () => {
        refetchRoleData();
      },
    },
  );

  const onSubmit = (data: FormProps) => {
    editOrganizationRole(data.name);
  };

  if (isLoading || isLoadingRoleData) return <CircularProgress />;

  return (
    <Box
      maxWidth={{
        xs: "100%",
        md: "75%",
        lg: "60%",
        xl: "40%",
      }}
      p={3}
    >
      <Box marginBottom={2}>
        <Typography variant="h6">
          {t("title", { name: roleData?.roleName })}
        </Typography>
      </Box>
      <Box marginBottom={2}>
        <SmartForm<FormProps> onSubmit={onSubmit} methods={formMethods}>
          <SmartTextField<FormProps>
            fullWidth
            name="name"
            label={t("organization-management:name")}
            required
          />
          <SmartSubmit>{t("general:save")}</SmartSubmit>
        </SmartForm>
      </Box>
      <PermissionEdit
        permissions={permissions!}
        onSubmit={(data) => editPermissions(data.permissions)}
      />
    </Box>
  );
};

export default OrganizationRolePage;
